import { useKeycloak } from '@react-keycloak/web';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { AppContent } from '../AppContent';
import { AuthSplash } from '../AuthSplash';
import LogoutCallback from '../LogoutCallback';
import { modelAction } from '../../actions';
import { LogoutContext } from '../../contexts/LogoutContext';
import { getStorage, setStorage, STORAGE_KEY } from '../../helpers/storage';
import { useNotification } from '../../hooks/useNotification';
import { User } from '../../types';

export const KeycloakApp = React.memo(() => {
  const { keycloak, initialized } = useKeycloak();
  const { i18n, t } = useTranslation();
  const location = useLocation();
  const dispatch = useDispatch();
  const models = useSelector((state: any) => state.model?.models || []);
  const {
    isSupported,
    state: pushState,
    requestPermission,
    subscribeToNotifications,
  } = useNotification();

  const notificationCheckedRef = useRef(false);
  const [authError, setAuthError] = React.useState<string | null>(null);
  const [retryCount, setRetryCount] = React.useState(0);

  // Check if current route is logout callback - handle it before Keycloak authentication
  const isLogoutCallback = location.pathname === '/logout-callback';

  // Handle retry logic
  const handleLogin = React.useCallback(() => {
    try {
      setAuthError(null);
      console.log('🔄 Attempting login redirect...');
      keycloak.login({
        redirectUri: window.location.href,
        prompt: 'login',
      });
    } catch (error) {
      console.error('❌ Error during login redirect:', error);
      setAuthError('Failed to redirect to login. Please try again.');
      setRetryCount((prev) => prev + 1);
    }
  }, [keycloak]);

  // Auto-retry on login_required error
  React.useEffect(() => {
    if (retryCount > 0 && retryCount < 3) {
      const timer = setTimeout(() => {
        console.log(`🔄 Auto-retrying login (attempt ${retryCount + 1})...`);
        handleLogin();
      }, 2000 * retryCount); // Exponential backoff

      return () => clearTimeout(timer);
    }
  }, [retryCount, handleLogin]);

  // Prevent Keycloak from redirecting when on logout callback
  useEffect(() => {
    if (isLogoutCallback && keycloak && !keycloak.authenticated) {
      console.log('🔍 On logout callback route, preventing Keycloak redirect...');
      // Override Keycloak's login method temporarily
      const originalLogin = keycloak.login;
      keycloak.login = () => {
        console.log('🚫 Login call blocked on logout callback route');
        return Promise.resolve(false);
      };

      // Restore original login method after cleanup
      return () => {
        keycloak.login = originalLogin;
      };
    }
  }, [isLogoutCallback, keycloak]);

  useEffect(() => {
    if (initialized && keycloak.authenticated) {
      // Store user info in localStorage
      const userData: User = {
        username: keycloak.tokenParsed?.preferred_username || '',
        fullName: keycloak.tokenParsed?.name || '',
        email: keycloak.tokenParsed?.email || '',
        token: keycloak.token || '',
        tokenParsed: keycloak.tokenParsed || {},
      };

      setStorage(STORAGE_KEY.USER, JSON.stringify(userData));
      setStorage(STORAGE_KEY.TOKEN, userData.token);
      setStorage(STORAGE_KEY.ID_TOKEN, keycloak.idToken || '');
      setStorage(STORAGE_KEY.ID, keycloak.tokenParsed?.sub || '');

      // Sync locale from token to localStorage and i18n ONLY on first login
      if (userData.tokenParsed.locale) {
        const currentLocale = getStorage(STORAGE_KEY.I18_NEXT_LANGUAGE);
        if (!currentLocale) {
          // First login - set locale from token
          const localeMapping: { [key: string]: string } = {
            'zh-CN': 'zh',
            'pt-BR': 'pt',
            'en-US': 'en',
          };
          const mappedLocale =
            localeMapping[userData.tokenParsed.locale] || userData.tokenParsed.locale;
          setStorage(STORAGE_KEY.I18_NEXT_LANGUAGE, mappedLocale);
          i18n.changeLanguage(mappedLocale);
        }
      }

      // Fetch models once when user is authenticated
      if (!models || models.length === 0) {
        console.log('🔄 Fetching models on authentication...');
        dispatch(modelAction.fetchModelsRequest());
      }
    } else if (initialized && !keycloak.authenticated) {
      // User has logged out, reset notification check flag for next login
      console.log('🚪 User logged out, resetting notification check flag...');
      notificationCheckedRef.current = false;
    }
  }, [
    initialized,
    keycloak.authenticated,
    keycloak.token,
    keycloak.tokenParsed,
    keycloak.idToken,
    i18n,
    dispatch,
    models,
  ]);

  // Check notification permission and subscription when user is authenticated
  useEffect(() => {
    // Only check once per session
    if (notificationCheckedRef.current) {
      console.log('🔔 Notification already checked, skipping');
      return;
    }

    // Only run when user is authenticated and app is ready
    if (!keycloak.authenticated || !isSupported) {
      return;
    }

    const checkNotification = async () => {
      // Wait a bit for app to fully load
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Detect Safari browser
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

      console.log('🔔 Notification Check on Auth:', {
        isSupported,
        isAuthenticated: keycloak.authenticated,
        permission: pushState?.permission,
        isSubscribed: pushState?.isSubscribed,
        isSafari,
      });

      if (isSupported && keycloak.authenticated) {
        notificationCheckedRef.current = true;

        // Check if already subscribed by looking at localStorage
        const existingSubscriptionId = localStorage.getItem('push_subscription_id');
        console.log('🔔 Existing subscription ID:', existingSubscriptionId);

        if (existingSubscriptionId) {
          console.log('🔔 Already subscribed, skipping subscription');
          return;
        }

        console.log('🔔 pushState:', pushState);

        // For Safari, don't auto-request permissions as it requires user gesture
        if (isSafari) {
          console.log(
            '🔍 Safari detected - skipping auto permission request (requires user gesture)'
          );
          return;
        }

        // Auto-request permission if not granted yet (for non-Safari browsers)
        if (pushState?.permission === 'default') {
          try {
            console.log('🔔 Auto-requesting notification permission...');
            const permissionGranted = await requestPermission();
            console.log('🔔 Auto-requesting notification permission... (COMPLETED)');

            // If permission granted, subscribe immediately
            if (permissionGranted) {
              try {
                console.log('🔔 Auto-subscribing to push notifications...');
                await subscribeToNotifications();
                console.log('🔔 Auto-subscribing to push notifications... (COMPLETED)');
              } catch (error) {
                console.error('❌ Auto-subscription failed:', error);
              }
            }
          } catch (error) {
            console.error('❌ Auto-permission request failed:', error);
          }
        }

        // Auto-subscribe if permission already granted but not subscribed
        if (pushState?.permission === 'granted' && !pushState?.isSubscribed) {
          try {
            console.log('🔔 Auto-subscribing to push notifications...');
            await subscribeToNotifications();
            console.log('🔔 Auto-subscribing to push notifications... (COMPLETED)');
          } catch (error) {
            console.error('❌ Auto-subscription failed:', error);
          }
        }
      }
    };

    checkNotification();
  }, [keycloak.authenticated, isSupported, pushState, requestPermission, subscribeToNotifications]); // Include all dependencies but with early returns

  const logout = async () => {
    try {
      console.log('🚪 Starting logout process...');

      // Give a small delay to allow any pending operations to complete
      await new Promise((resolve) => setTimeout(resolve, 500));

      console.log('🔄 Redirecting to Keycloak logout...');
      window.location.href = `${keycloak.authServerUrl}/realms/${keycloak.realm}/protocol/openid-connect/logout?client_id=${keycloak.clientId}&post_logout_redirect_uri=${window.location.origin}/logout-callback`;
    } catch (error) {
      console.error('❌ Error during logout:', error);
      // Still redirect even if there's an error
      window.location.href = `${keycloak.authServerUrl}/realms/${keycloak.realm}/protocol/openid-connect/logout?client_id=${keycloak.clientId}&post_logout_redirect_uri=${window.location.origin}/logout-callback`;
    }
  };

  // Handle logout callback route before Keycloak authentication check
  if (isLogoutCallback) {
    console.log('🔍 Logout callback detected, rendering LogoutCallback component');
    return <LogoutCallback />;
  }

  if (!initialized) {
    return <AuthSplash title={i18n.t('logout.redirectingToLogin')} />;
  }

  // If not authenticated and not on logout callback, redirect to login
  if (!keycloak.authenticated) {
    console.log('🔍 User not authenticated, redirecting to login...');

    // Use setTimeout to prevent blocking the render
    setTimeout(() => {
      handleLogin();
    }, 100);

    return (
      <AuthSplash
        title={authError ? t('common.authError') : i18n.t('logout.redirectingToLogin')}
        error={authError}
        retryCount={retryCount}
        retryLabel={t('common.retryLogin')}
        retryAttemptLabel={t('common.retryAttempt')}
        onRetry={() => {
          setRetryCount(0);
          setAuthError(null);
          handleLogin();
        }}
      />
    );
  }

  return (
    <LogoutContext.Provider value={{ logout }}>
      <AppContent
        isAuthenticated={keycloak.authenticated}
        isLoading={!initialized}
        user={{
          username: keycloak.tokenParsed?.preferred_username || '',
          fullName: keycloak.tokenParsed?.name || '',
          email: keycloak.tokenParsed?.email || '',
          token: keycloak.token || '',
          tokenParsed: keycloak.tokenParsed || {},
        }}
        login={() => keycloak.login()}
        logout={logout}
      />
    </LogoutContext.Provider>
  );
});
